import { Component, createRef } from "react"

/* Modal */
import Modal from "react-modal"

/* Components */
import { Input, Icon, Avatar } from "../../components"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* REST API */
import { save } from "../../api/Service"

/* Widgets */
import { toast } from "../common"
import CategoryPicker from "./CategoryPicker"

/* Shared */
import { duration, money } from "./shared"
import { organizationIcon } from "../organization/shared"
import { toneOf } from "../category/shared"

/* Constants */
import { env } from "../../constants"

Modal.setAppElement("#root")


/* Duration presets of the form, the rest is entered by hand */
const DURATIONS = [15, 30, 45, 60, 90, 120]

/* What the server says about a field it did not take */
const ERRORS = {
    INVALID_NAME: ["_name", "Введите название услуги"],
    INVALID_PRICE: ["_price", "Введите стоимость услуги"],
    INVALID_END_PRICE: ["_endPrice", "Верхняя цена должна быть больше нижней"],
    INVALID_DISCOUNT: ["_discount", "Скидка должна быть меньше цены"],
    INVALID_DURATION: ["_sessionTime", "От 5 минут до 12 часов"]
}


/* Widget Service Modal — the card of one service of the platform. The masters who provide it are
   the business of the point and stay in its card; here is what the client reads in the price list.
   It is opened by the reference: this._service.current.open(item) */
class ServiceModal extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            saving: false,
            item: null,

            name: "",
            categoryID: 0,
            fixed: true,
            price: "",
            endPrice: "",
            discount: "",
            sessionTime: "",
            active: 1
        }

        this._picker = createRef()
    }

    /* Open the card of a line of the list */
    open = item => {

        const number = value => value ? `${parseInt(value)}` : ""

        this.setState({
            visible: true,
            saving: false,
            item,
            name: item.name ? `${item.name}`.trim() : "",
            categoryID: item.categoryID ? parseInt(item.categoryID) : 0,
            fixed: parseInt(item.isFixedPrice) !== 0,
            price: number(item.price),
            endPrice: number(item.endPrice),
            discount: number(item.discount),
            sessionTime: number(item.sessionTime),
            active: parseInt(item.active) === 0 ? 0 : 1
        })
    }

    close = () => {

        /* The request is already on its way */
        if (this.state.saving) {
            return
        }

        this.setState({ visible: false })
    }

    /* Only digits go into the fields of money and minutes */
    digits = (field, value) => {
        this.setState({ [field]: `${value}`.replace(/\D/g, "").slice(0, 9) })
    }

    /* The percent of the discount — the client sees the same badge in the app */
    percent = () => {

        const { price, discount } = this.state

        if (!/^\d+$/.test(price) || !/^\d+$/.test(discount) || parseInt(discount) >= parseInt(price)) {
            return null
        }

        return Math.round((1 - parseInt(discount) / parseInt(price)) * 100)
    }

    save = () => {

        /* Fields */
        const { item, name, categoryID, fixed, price, endPrice, discount, sessionTime, active, saving } = this.state
        const { onSaved } = this.props

        if (saving) {
            return
        }

        if (name.trim().length === 0) {
            this._name.error("Введите название услуги")
            return
        }

        if (!/^\d+$/.test(price)) {
            this._price.error("Введите стоимость услуги")
            return
        }

        if (!fixed && (!/^\d+$/.test(endPrice) || parseInt(endPrice) <= parseInt(price))) {
            this._endPrice.error("Верхняя цена должна быть больше нижней")
            return
        }

        if (discount.length > 0 && parseInt(discount) >= parseInt(price)) {
            this._discount.error("Скидка должна быть меньше цены")
            return
        }

        if (!/^\d+$/.test(sessionTime) || parseInt(sessionTime) < 5 || parseInt(sessionTime) > 720) {
            this._sessionTime.error("От 5 минут до 12 часов")
            return
        }

        this.setState({ saving: true })

        save({
            id: item.id,
            name: name.trim(),
            categoryID: categoryID ? categoryID : null,
            isFixedPrice: fixed ? 1 : 0,
            price,
            endPrice: fixed ? null : endPrice,
            discount: discount.length > 0 ? discount : null,
            sessionTime,
            active
        }).then(response => {

            this.setState({ saving: false })

            if (response && response.status === 200) {
                this.setState({ visible: false })
                onSaved()
                toast.success("Услуга сохранена")
                return
            }

            const error = response && response.data ? ERRORS[response.data.code] : null

            if (error && this[error[0]]) {
                this[error[0]].error(error[1])
                return
            }

            toast.error("Не удалось сохранить услугу")
        }).catch(() => {
            this.setState({ saving: false })
            toast.error("Не удалось сохранить услугу")
        })
    }

    /* The line of the service as the client sees it in the price list */
    _preview = () => {

        /* Fields */
        const { item, name, fixed, price, endPrice, discount, sessionTime } = this.state

        const off = this.percent()
        const ready = /^\d+$/.test(price)

        let text = "Цена не указана"

        if (ready && fixed) {
            text = money(off !== null ? discount : price)
        }

        if (ready && !fixed) {
            text = `от ${money(off !== null ? discount : price)}${/^\d+$/.test(endPrice) ? ` до ${money(endPrice)}` : ""}`
        }

        return (
            <div className="service-modal-preview">

                <div className="service-modal-preview-label">Так видит клиент</div>

                <div className="service-modal-preview-line">
                    <div className="service-modal-preview-dot" style={{ background: item.color ? item.color : "#90a4ae" }} />
                    <div className="service-modal-preview-text">
                        <b>{name.trim().length > 0 ? name.trim() : "Название услуги"}</b>
                        <span>{duration(sessionTime)}</span>
                    </div>
                    <div className="service-modal-preview-price">
                        {off !== null && <s>{money(price)}</s>}
                        <b>{text}</b>
                        {off !== null && <em>−{off}%</em>}
                    </div>
                </div>

            </div>
        )
    }

    _body = () => {

        /* Fields */
        const { item, name, categoryID, fixed, price, endPrice, discount, sessionTime, active, saving } = this.state
        const { categories } = this.props

        const category = categories.find(element => parseInt(element.id) === parseInt(categoryID))
        const tone = category ? toneOf(category.typeID) : null
        const image = item.organizationImage ? `${env.mediapoint}organizations/${item.organizationImage}` : null

        return (
            <div className="wide-modal-body">

                <div className="service-modal-point">
                    <Avatar className="service-modal-point-avatar" src={image} alt={item.organization}>
                        {organizationIcon()}
                    </Avatar>
                    <div className="service-modal-point-text">
                        <span>Точка</span>
                        <b>{item.organization}</b>
                    </div>
                    <div className="service-modal-point-stat">
                        <b>{item.bookings}</b>
                        <span>записей</span>
                    </div>
                </div>

                {this._preview()}

                <div className="wide-modal-label">Название</div>
                <Input ref={ref => this._name = ref} label="Название услуги" maxLength={150} value={name} onChange={event => this.setState({ name: event.target.value })} />

                <div className="wide-modal-label">Категория</div>
                <div className="wide-modal-hint">По ней приложение раскладывает прайс-лист точки. Без категории услуга попадает в «Другие».</div>

                <div
                    onClick={event => this._picker.current.open(event.currentTarget, { selected: categoryID, none: true, title: "Категория услуги", onSelect: value => this.setState({ categoryID: value === "none" ? 0 : value }) })}
                    className={`service-modal-category ${category ? "" : "empty"}`}
                    style={tone ? { "--tone": tone.accent, "--tone-soft": tone.soft } : {}}
                >
                    <div className="service-modal-category-icon">
                        <Icon name={category ? "category" : "alert"} size={17} width={2} />
                    </div>
                    <div className="service-modal-category-text">
                        <b>{category ? category.name : "Без категории"}</b>
                        <span>{category ? category.typeName : "Выберите, чтобы услуга встала в свой раздел"}</span>
                    </div>
                    <Icon name="down" size={16} width={2.2} />
                </div>

                {!categoryID && item.suggestion &&
                    <div onClick={() => this.setState({ categoryID: item.suggestion.categoryID })} className="service-modal-suggestion">
                        <Icon name="sparkle" size={15} width={1.8} />
                        <span>Подсказка: <b>{item.suggestion.name}</b></span>
                        <em>Выбрать</em>
                    </div>
                }

                <div className="wide-modal-label">Цена</div>

                <div className="service-modal-switch">
                    <div onClick={() => this.setState({ fixed: true })} className={fixed ? "active" : ""}>Фиксированная</div>
                    <div onClick={() => this.setState({ fixed: false })} className={fixed ? "" : "active"}>Диапазон «от — до»</div>
                    <i className={fixed ? "" : "right"} />
                </div>

                <div className="organization-details-inputs">
                    <Input ref={ref => this._price = ref} label={fixed ? "Стоимость, ₸" : "Цена от, ₸"} value={price} onChange={event => this.digits("price", event.target.value)} />
                    {!fixed && <Input ref={ref => this._endPrice = ref} label="Цена до, ₸" value={endPrice} onChange={event => this.digits("endPrice", event.target.value)} />}
                    <Input ref={ref => this._discount = ref} label="Цена по скидке, ₸" hint="Пусто — без скидки" value={discount} onChange={event => this.digits("discount", event.target.value)} />
                </div>

                <div className="wide-modal-label">Длительность</div>

                <div className="service-modal-durations">
                    {DURATIONS.map(minutes =>
                        <div key={`${minutes}`} onClick={() => this.setState({ sessionTime: `${minutes}` })} className={`service-modal-duration ${parseInt(sessionTime) === minutes ? "active" : ""}`}>
                            {duration(minutes)}
                        </div>
                    )}
                </div>

                <Input ref={ref => this._sessionTime = ref} label="Длительность, минут" value={sessionTime} onChange={event => this.digits("sessionTime", event.target.value)} />

                <div className="wide-modal-label">В прайс-листе</div>

                <div onClick={() => this.setState({ active: active ? 0 : 1 })} className={`service-modal-active ${active ? "on" : ""}`}>
                    <div className="service-modal-active-text">
                        <b>{active ? "Услуга в прайс-листе" : "Услуга в архиве"}</b>
                        <span>{active ? "Клиенты видят её и записываются" : "Клиенты её не видят, старые записи остаются"}</span>
                    </div>
                    <div className="category-card-toggle static"><i /></div>
                </div>

                <div onClick={() => this.save()} className={`organization-details-button sticky ${saving ? "disabled" : ""}`}>
                    {saving ? <LoadingOutlined /> : "Сохранить"}
                </div>

                <CategoryPicker ref={this._picker} categories={categories} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, item } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="wide-modal"
                overlayClassName="wide-modal-overlay"
                closeTimeoutMS={200}
            >

                <div className="wide-modal-header">
                    <h2>Услуга</h2>
                    <div onClick={this.close} className="wide-modal-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>
                </div>

                {item && this._body()}

            </Modal>
        )
    }
}

export default ServiceModal
